// views/admin/error/ExpirationPage.jsx
import React, { useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  VStack,
  Icon,
  Divider,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FiLogOut, FiAlertTriangle } from 'react-icons/fi';
import { useModalColors } from 'hooks/useModalColors';

const MotionBox = motion(Box);
const MotionButton = motion(Button);

export default function ExpirationPage({ data }) {
  const colors = useModalColors();

  // Use data from props or fallback to stored data
  const errorData = data || {};
  
  const { 
    error = 'NO_SUBSCRIPTION',
    message = 'No active subscription found. Please purchase a plan.',
    forceLogout = true
  } = errorData;

  // Simple logout - just clear storage and redirect
  const handleLogout = () => {
    // Held over the wipe so the next sign-in can skip the workspace picker.
    // The /auth redirect below lands on the resolver, which re-validates both
    // before anything trusts them.
    const workspaces = localStorage.getItem('workspaces');
    const lastWorkspace = localStorage.getItem('lastWorkspace');

    // Clear all storage
    localStorage.clear();
    sessionStorage.clear();
    if (workspaces) localStorage.setItem('workspaces', workspaces);
    if (lastWorkspace) localStorage.setItem('lastWorkspace', lastWorkspace);
    
    // Redirect to login
    window.location.href = '/auth';
  };

  // Auto logout after 5 minutes
  useEffect(() => {
    const timer = setTimeout(() => {
      handleLogout();
    }, 5 * 60 * 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg={colors.bgDeep}
      px={4}
      py={8}
    >
      <MotionBox
        w="100%"
        maxW="480px"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Box
          bg={colors.bg}
          borderRadius="2xl"
          boxShadow={colors.modalShadow}
          overflow="hidden"
          border="1px solid"
          borderColor={colors.borderColor}
        >
          {/* Subtle Header */}
          <Box 
            p={6} 
            borderBottom="1px solid" 
            borderColor={colors.borderColor}
            bg={colors.bgDeep}
          >
            <Flex align="center" gap={3}>
              <Box
                bg={colors.badgeWarningBg}
                p={2}
                borderRadius="lg"
                color={colors.badgeWarningText}
              >
                <Icon as={FiAlertTriangle} w={5} h={5} />
              </Box>
              <Text fontWeight="600" color={colors.headingText} fontSize="md">
                {error === 'NO_SUBSCRIPTION' ? 'Subscription Required' : 'Access Restricted'}
              </Text>
            </Flex>
          </Box>

          <Box p={8}>
            <VStack spacing={6} align="stretch">
              {/* Icon */}
              <Flex justify="center">
                <Box
                  bg={colors.bgDeep}
                  p={6}
                  borderRadius="full"
                  border="1px solid"
                  borderColor={colors.borderColor}
                >
                  <Icon as={FiAlertTriangle} w={12} h={12} color={colors.badgeWarningText} />
                </Box>
              </Flex>

              {/* Error Details */}
              <Box textAlign="center">
                <Heading size="lg" mb={3} color={colors.headingText}>
                  {error === 'NO_SUBSCRIPTION' ? 'No Active Subscription' : 'Subscription Expired'}
                </Heading>
                <Text color={colors.bodyText} fontSize="sm" lineHeight="1.7">
                  {message || 'Your subscription is no longer active. Please contact your administrator to restore access.'}
                </Text>
              </Box>

              <Divider borderColor={colors.borderColor} />

              {/* Error Info */}
              <Box>
                <Text fontSize="xs" color={colors.mutedText} textAlign="center">
                  Error Reference: <Text as="span" color={colors.accentGold} fontFamily="mono">{error}</Text>
                </Text>
              </Box>

              <Divider borderColor={colors.borderColor} />

              {/* Logout Button */}
              <MotionButton
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                variant="outline"
                borderColor={colors.borderColor}
                color={colors.bodyText}
                size="lg"
                leftIcon={<FiLogOut />}
                onClick={handleLogout}
                py={6}
                w="full"
                _hover={{
                  borderColor: colors.accentGold,
                  color: colors.accentGold,
                  bg: colors.secondaryBtnHoverBg,
                }}
                transition="all 0.2s ease"
              >
                Sign Out
              </MotionButton>
            </VStack>
          </Box>
        </Box>
      </MotionBox>
    </Flex>
  );
}